import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShoppingBag, Users, Sparkles, Gift, ListChecks, Star, Lock } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ACTIONS = [
  { key: "individual",  Icon: ShoppingBag, color: "#D81B8C", path: "shop",       freezeGroup: "normal"  },
  { key: "collective",  Icon: Users,       color: "#3FA535", path: "collective", freezeGroup: "normal"  },
  { key: "custom",      Icon: Sparkles,    color: "#6B2C8F", path: "custom",     freezeGroup: "normal"  },
  { key: "offered",     Icon: Gift,        color: "#D81B8C", path: "offered",    freezeGroup: "none"    },
  { key: "wishlist",    Icon: ListChecks,  color: "#3FA535", path: "wishlist",   freezeGroup: "none"    },
  { key: "special",     Icon: Star,        color: "#6B2C8F", path: "special",    freezeGroup: "special" },
];

export default function EventActions({ event }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { code } = useParams();

  const frozen = !!event?.frozen;
  const specialFrozen = !!event?.special_frozen;

  const isDisabled = (freezeGroup) => {
    if (freezeGroup === "none") return false;
    if (freezeGroup === "special") return specialFrozen;
    return frozen; // "normal"
  };

  return (
    <div className="mt-10" data-testid="event-actions">
      <h2 className="font-display text-2xl sm:text-3xl font-black text-[#2A0E3C] mb-6 text-center">
        {t("event.actionsTitle")}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        {ACTIONS.map(({ key, Icon, color, path, freezeGroup }) => {
          const disabled = isDisabled(freezeGroup);
          return (
            <button
              key={key}
              data-testid={`action-${key}`}
              disabled={disabled}
              aria-disabled={disabled}
              onClick={() => !disabled && navigate(`/event/${code}/${path}`)}
              className={`group relative bg-white rounded-2xl p-5 sm:p-6 border border-[#F2E2EC] text-left overflow-hidden transition-all ${
                disabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:border-[#D81B8C]/40 hover:shadow-lg hover:-translate-y-0.5"
              }`}
            >
              <div
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: `${color}15`, color }}
              >
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2.2} />
              </div>
              <div className="font-display text-[15px] sm:text-base font-black text-[#2A0E3C] leading-tight">
                {t(`actions.${key}.title`)}
              </div>
              <div className="mt-1 text-[11px] sm:text-xs text-[#5C4664] leading-snug">
                {t(`actions.${key}.desc`)}
              </div>
              {disabled && (
                <div
                  data-testid={`action-${key}-locked`}
                  className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center"
                >
                  <Lock className="w-3.5 h-3.5" />
                </div>
              )}
              {!disabled && (
                <div
                  className="absolute top-0 right-0 w-16 h-16 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background: `radial-gradient(circle at top right, ${color}25, transparent 70%)`,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
